import { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { Building, Grid3x3, Wrench, MapPin, Eye, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { supabase } from '../../lib/supabase';
import type { DatabaseProperty, DatabaseCategory } from '../../lib/supabase';

interface Property {
  id: string;
  title: string;
  location: string;
  type: 'sale' | 'rent';
  category: string;
  price: number;
  featured: boolean;
}

interface Category {
  id: string;
  name: string;
  count: number;
}

export default function AdminDashboard() {
  const [properties, setProperties] = useState<Property[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [facilitiesCount] = useState(8);
  const [nearbyCount] = useState(6);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch properties
      const { data: propertiesData, error: propertiesError } = await supabase
        .from('properties')
        .select('id, title, location, type, category, price, featured, created_at')
        .order('created_at', { ascending: false });
      
      if (propertiesError) throw propertiesError;
      
      // Fetch categories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('categories')
        .select('id, name');
      
      if (categoriesError) throw categoriesError;
      
      // Get property counts per category
      const categoriesWithCount = await Promise.all(
        (categoriesData || []).map(async (cat: { id: string; name: string }) => {
          const { count } = await supabase
            .from('properties')
            .select('*', { count: 'exact', head: true })
            .eq('category_id', cat.id);
          
          return {
            id: cat.id,
            name: cat.name,
            count: count || 0,
          };
        })
      );
      
      setProperties(propertiesData || []);
      setCategories(categoriesWithCount);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };
  const stats = [
    {
      title: 'إجمالي العقارات',
      value: properties.length,
      icon: Building,
      color: 'bg-blue-500',
      link: '/admin/properties',
    },
    {
      title: 'التصنيفات',
      value: categories.length,
      icon: Grid3x3,
      color: 'bg-green-500',
      link: '/admin/categories',
    },
    {
      title: 'المرافق',
      value: facilitiesCount,
      icon: Wrench,
      color: 'bg-[#D4AF37]',
      link: '/admin/facilities',
    },
    {
      title: 'الأماكن القريبة',
      value: nearbyCount,
      icon: MapPin,
      color: 'bg-purple-500',
      link: '/admin/nearby',
    },
  ];

  const recentProperties = properties.slice(0, 5);
  const forSale = properties.filter(p => p.type === 'sale').length;
  const forRent = properties.filter(p => p.type === 'rent').length;
  const featured = properties.filter(p => p.featured).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">لوحة التحكم</h1>
        <p className="text-gray-600">مرحباً بك في لوحة التحكم الخاصة بإدارة العقارات</p>
      </div>

      {/* Stats Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link key={stat.title} to={stat.link}>
              <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">{stat.title}</p>
                      <p className="text-3xl font-bold text-gray-800">{stat.value}</p>
                    </div>
                    <div className={`w-14 h-14 ${stat.color} rounded-lg flex items-center justify-center`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        {/* Property Types */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">أنواع العقارات</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">للبيع</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500"
                      style={{ width: properties.length > 0 ? `${(forSale / properties.length) * 100}%` : '0%' }}
                    ></div>
                  </div>
                  <span className="font-bold text-gray-800 w-8">{forSale}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">للإيجار</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-blue-500"
                      style={{ width: properties.length > 0 ? `${(forRent / properties.length) * 100}%` : '0%' }}
                    ></div>
                  </div>
                  <span className="font-bold text-gray-800 w-8">{forRent}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">مميز</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-[#D4AF37]"
                      style={{ width: properties.length > 0 ? `${(featured / properties.length) * 100}%` : '0%' }}
                    ></div>
                  </div>
                  <span className="font-bold text-gray-800 w-8">{featured}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">إجراءات سريعة</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Link to="/admin/properties">
                <button className="w-full text-right px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-700">
                  إضافة عقار جديد
                </button>
              </Link>
              <Link to="/admin/categories">
                <button className="w-full text-right px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-700">
                  إدارة التصنيفات
                </button>
              </Link>
              <Link to="/admin/facilities">
                <button className="w-full text-right px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-700">
                  إدارة المرافق
                </button>
              </Link>
              <Link to="/" target="_blank">
                <button className="w-full text-right px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-700">
                  عرض الموقع
                </button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Categories Overview */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">التصنيفات</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {categories.slice(0, 5).map(cat => (
                <div key={cat.id} className="flex items-center justify-between">
                  <span className="text-gray-700">{cat.name}</span>
                  <span className="font-bold text-gray-800">{cat.count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Properties */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">أحدث العقارات</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">العنوان</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">النوع</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">التصنيف</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">السعر</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">الحالة</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {recentProperties.map(property => (
                  <tr key={property.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="font-medium text-gray-800">{property.title}</div>
                      <div className="text-sm text-gray-600">{property.location}</div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                        property.type === 'rent' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {property.type === 'rent' ? 'للإيجار' : 'للبيع'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-700">{property.category}</td>
                    <td className="py-3 px-4 font-medium text-gray-800">
                      {property.price.toLocaleString('ar-EG')} ج.م.
                    </td>
                    <td className="py-3 px-4">
                      {property.featured && (
                        <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-[#FFF9E6] text-[#8B7109]">
                          مميز
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <Link to={`/property/${property.id}`} target="_blank">
                        <button className="text-blue-600 hover:text-blue-800">
                          <Eye className="w-5 h-5" />
                        </button>
                      </Link>
                    </td>
                  </tr>
                ))}
                {recentProperties.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-gray-500">
                      لا توجد عقارات متاحة
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}